import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useContext, useMemo, useState } from "react";
import QRCode from "react-qr-code";
import { Navigate, useLocation } from "react-router-dom";
import { MFAInput } from "../../components/auth/MFAInput";
import { AuthContext } from "../../context/AuthContext";

export function MFASetup() {
  const { confirmMFACode } = useContext(AuthContext);
  const { state } = useLocation();
  const totpSetupUri: string | undefined = state?.totpSetupUri;

  if (!totpSetupUri) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Box display="flex" justifyContent="center">
      <Card sx={{ minWidth: 400, maxWidth: 480 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={3} alignItems="center">
            <Stack spacing={1} alignItems="center">
              <Typography variant="h5" fontWeight={600}>
                Set up two-factor authentication
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                Scan the QR code with your authenticator app, then enter the
                6-digit code it generates.
              </Typography>
            </Stack>
            <Box
              sx={{
                p: 2,
                bgcolor: "common.white",
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                lineHeight: 0,
              }}
            >
              <QRCode value={totpSetupUri} size={180} />
            </Box>
            <SecretKeyFallback totpSetupUri={totpSetupUri} />
            <MFAInput onSubmit={confirmMFACode} />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

function SecretKeyFallback({ totpSetupUri }: { totpSetupUri: string }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const secret = useMemo(() => {
    try {
      return new URL(totpSetupUri).searchParams.get("secret") ?? "";
    } catch {
      return "";
    }
  }, [totpSetupUri]);

  const formattedSecret = useMemo(
    () => secret.match(/.{1,4}/g)?.join(" ") ?? "",
    [secret],
  );

  if (!secret) return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!open) {
    return (
      <Button size="small" onClick={() => setOpen(true)}>
        Can't scan? Show secret key
      </Button>
    );
  }

  return (
    <Stack spacing={1} sx={{ width: "100%" }}>
      <Typography variant="caption" color="text.secondary" textAlign="center">
        Enter this key in your authenticator app instead:
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.5,
          bgcolor: "action.hover",
          borderRadius: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", ui-monospace, monospace',
            fontSize: "1.15rem",
            fontWeight: 500,
            letterSpacing: 2,
            fontVariantNumeric: "slashed-zero",
            fontFeatureSettings: '"zero", "ss01"',
            flexGrow: 1,
            textAlign: "center",
            wordBreak: "keep-all",
            overflowWrap: "normal",
          }}
        >
          {formattedSecret}
        </Typography>
        <Tooltip title={copied ? "Copied!" : "Copy"}>
          <IconButton size="small" onClick={handleCopy}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Stack>
  );
}
